<script setup>
import { computed, ref } from "vue";
import SlideMenuButton from "@/v2/components/SlideMenu/shared/SlideMenuButton.vue";
import useFocusTrap from "@/v2/shared/useFocusTrap.ts";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: true,
  },
  isOverlay: {
    type: Boolean,
    default: false,
  },
  isLeft: {
    type: Boolean,
    default: true,
  },
  useSurfaceColors: {
    type: Boolean,
    default: true,
  },
  hasFade: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["close"]);
const wrapperRef = ref(null);

const componentClasses = computed(() => ({
  "slide-menu-wrapper": true,
  "slide-menu-wrapper--open": props.isOpen,
  "slide-menu-wrapper--left": props.isLeft,
  "slide-menu-wrapper--override-surface-colors": !props.useSurfaceColors,
}));

useFocusTrap(wrapperRef, () => emit("close"));
</script>

<template>
  <div ref="wrapperRef" :class="componentClasses" focus-theme="light">
    <div v-if="hasFade" class="slide-menu-wrapper__fade" />

    <div class="slide-menu-wrapper__slot">
      <slot />
    </div>
    <SlideMenuButton
      v-if="isOpen"
      :is-left="isLeft"
      :is-overlay="isOverlay"
      @click="$emit('close')"
    />
  </div>
</template>

<style scoped lang="scss">
@import "@/v2/styles/main";
@import "@/v2/components/SlideMenu/slide-menu";

/* clip-path variables */
$clip-path-closed-right: circle(0% at 100% 0%);
$clip-path-closed-left: circle(0% at 0% 0%);
$clip-path-open: circle(100% at 50% 50%);

.slide-menu-wrapper {
  clip-path: $clip-path-closed-right;
  transition: clip-path $slide-menu-transition-time $slide-menu-easing;
  background: var(--main-menu-surface);
  height: 100%;
  overflow: hidden;

  .slide-menu-wrapper__slot {
    width: 100%;
    transform: translateX(100%);
    transition: transform
      calc($slide-menu-transition-time + $slide-menu-transition-time-offset)
      $slide-menu-easing;
    text-wrap: nowrap;
    height: 100%;
    overflow-y: scroll;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .slide-menu-wrapper__fade {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 164px;
    background: linear-gradient(
      to bottom,
      rgba(0 0 0 / 0%) 0,
      var(--main-menu-surface) 100%
    );
    z-index: 1;
    pointer-events: none;
  }

  &--override-surface-colors.slide-menu-wrapper {
    background: var(--side-menu-background-color);

    .slide-menu-wrapper__fade {
      background: linear-gradient(
        to bottom,
        rgba(0 0 0 / 0%) 0,
        var(--side-menu-background-color) 100%
      );
    }
  }

  &--left {
    clip-path: $clip-path-closed-left;
    transition: clip-path $slide-menu-transition-time $slide-menu-easing;

    .slide-menu-wrapper__slot {
      transform: translateX(-100%);
    }
  }

  &--open {
    clip-path: $clip-path-open;
    width: 100%;
    pointer-events: all;

    .slide-menu-wrapper__slot {
      transform: translateX(0%);
    }
  }
}
</style>
